import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';

import {Observable, Subscription} from 'rxjs';
import {map, tap} from 'rxjs/operators';

import {DateTime} from 'luxon';

import {SharedSettings} from '@shared/models/shared-settings';
import {buildQueryParams, url} from '@shared/helpers/url';

import {
    ArriveInboundLoadUpdate,
    InboundLoad,
    InboundLoadSearch,
    ProcessInboundLoadUpdate,
    UnloadInboundLoadUpdate
} from '../models/inbound-load';
import {SearchResult} from '@shared/models/search-result';
import {SequenceType} from '../models/sequence-type';
import {HistoryService} from "@shared/services/history.service";
import {HistoryResponse} from "@shared/models/history";

@Injectable({
    providedIn: 'root'
})
export class InboundLoadService {

    subscriptions: Subscription[];

    constructor(private http: HttpClient,
                private settings: SharedSettings,
                private historyService: HistoryService) {
    }

    getLoadDetail(id: string): Observable<InboundLoad> {
        return this.http.get<InboundLoad>(this.settings.apiBaseUrl + url`inboundLoads/${id}`).pipe(map(this.parseLoadValues));
    }

    getInboundLoads(siteId: string, date: DateTime, shiftId: number): Observable<InboundLoad[]> {
        if (siteId == null || shiftId == null) {
            return;
        }

        return this.http.get<InboundLoad[]>(this.settings.apiBaseUrl + url`inboundLoads?siteId=${siteId}&date=${date.toISODate()}&shiftId=${shiftId}`).pipe(
            tap(res => {
                res = res.map(l => this.parseLoadValues(l));
            }));
    }

    searchInboundLoads(request: InboundLoadSearch): Observable<SearchResult<InboundLoad>> {
        const queryParams = buildQueryParams(request).toString();

        return this.http.get<SearchResult<InboundLoad>>(`${this.settings.apiBaseUrl}InboundLoads/search?${queryParams}`).pipe(
            tap(res => {
                res.resultSet = res.resultSet.map(l => this.parseLoadValues(l));
            })
        );
    }

    getNextBolNumber(siteId: string): Observable<string> {
        return this.http.get(this.settings.apiBaseUrl + url`sites/${siteId}/sequences/${SequenceType.InboundBolNumber}`, {responseType: 'text'});
    }

    refNumberInUse(customerId: number, refNumber: string): Observable<number> {
        return this.http.get<number>(this.settings.apiBaseUrl + url`InboundLoads/RefNumInUse/${customerId}/${refNumber}`);
    }

    createInboundLoad(load: InboundLoad): Observable<InboundLoad> {
        return this.http.post<InboundLoad>(this.settings.apiBaseUrl + 'InboundLoads', this.serializeLoadValues(load))
            .pipe(map(this.parseLoadValues));
    }

    updateInboundLoad(load: InboundLoad): Observable<InboundLoad> {
        return this.http.put<InboundLoad>(this.settings.apiBaseUrl + 'InboundLoads', this.serializeLoadValues(load))
            .pipe(map(this.parseLoadValues));
    }

    arriveInboundLoad(id: number, update: ArriveInboundLoadUpdate): Observable<InboundLoad> {
        return this.http.put<InboundLoad>(this.settings.apiBaseUrl + url`InboundLoads/${id}/arrive`, {
            ...update,
            arrivedTimestamp: update.arrivedTimestamp.toISO()
        })
            .pipe(map(this.parseLoadValues));
    }

    unloadInboundLoad(id: number, update: UnloadInboundLoadUpdate): Observable<InboundLoad> {
        return this.http.put<InboundLoad>(this.settings.apiBaseUrl + url`InboundLoads/${id}/unload`, {
            ...update,
            unloadedTimestamp: update.unloadedTimestamp.toISO()
        })
            .pipe(map(this.parseLoadValues));
    }

    processInboundLoad(id: number, update: ProcessInboundLoadUpdate): Observable<InboundLoad> {
        return this.http.put<InboundLoad>(this.settings.apiBaseUrl + url`InboundLoads/${id}/process`, {
            ...update,
            unloadedTimestamp: update.unloadedTimestamp.toISO()
        })
            .pipe(map(this.parseLoadValues));
    }

    markTrailerLeft(loadId: number, trailerLeftWorkedShiftId: number): Observable<InboundLoad> {
        return this.http.post<InboundLoad>(this.settings.apiBaseUrl + url`InboundLoads/${loadId}/trailerLeft?leftYardWorkedShiftId=${trailerLeftWorkedShiftId}`, {})
            .pipe(map(this.parseLoadValues));
    }

    deleteInboundLoad(loadId: number) {
        return this.http.delete<InboundLoad>(this.settings.apiBaseUrl + url`InboundLoads/${loadId}`);
    }

    getHistory(siteId: string, id: number): Observable<HistoryResponse[]> {
        return this.historyService.getInboundLoadHistory(siteId, id);
    }

    private serializeLoadValues(load: InboundLoad): InboundLoad {
        return {
            ...load,
            arrivedTimestamp: <any>load.arrivedTimestamp?.toISO() ?? null,
            unloadedTimestamp: <any>load.unloadedTimestamp?.toISO() ?? null,
        };
    }

    private parseLoadValues(load: InboundLoad): InboundLoad {
        if (load.unloadedWorkedShift)
            load.unloadedWorkedShift.date = DateTime.fromISO(<any>load.unloadedWorkedShift.date);

        if (load.processedWorkedShift)
            load.processedWorkedShift.date = DateTime.fromISO(<any>load.processedWorkedShift.date);

        if (load.arrivedTimestamp)
            load.arrivedTimestamp = DateTime.fromISO(<any>load.arrivedTimestamp);

        if (load.unloadedTimestamp)
            load.unloadedTimestamp = DateTime.fromISO(<any>load.unloadedTimestamp);

        load.totalQuantity = load.inboundLoadLineItems.reduce((acc, item) => acc + item.quantity, 0);

        return load;
    }
}
